/**
 * useImportAccountingShipments - Hook para importar accounting shipments desde Excel
 *
 * POST /invoices/shipments/import
 * Sube un Excel, crea accounting_shipments y descarga el Excel de resultado.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export const useImportAccountingShipments = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { toast } = useToast();
  const [isImporting, setIsImporting] = useState(false);

  const importAccountingShipments = useCallback(
    async (file: File, onSuccess?: () => void) => {
      setIsImporting(true);

      try {
        const adapter = new ShipmentsApiAdapter(createHttpClient(apiClientWH));
        const { blob, filename } = await adapter.importAccountingShipments(file);

        downloadBlob(blob, filename);

        toast({
          title: t('import.success.title', 'Importación completada'),
          description: t('import.success.description', 'Se ha descargado el Excel con el resultado fila a fila.'),
          variant: 'default',
        });

        onSuccess?.();
      } catch (error: any) {
        const code = error?.message;
        let description = error?.message || t('import.error.unexpected', 'Error inesperado al importar el Excel.');

        if (code === 'INVALID_FILE_TYPE') {
          description = t('import.error.invalid_type', 'Formato no permitido. Usa .xlsx, .xls, .xlsm, .xltx o .xltm.');
        } else if (code === 'FILE_TOO_LARGE') {
          description = t('import.error.too_large', 'El archivo supera el límite de 10 MB.');
        }

        toast({
          title: t('import.error.title', 'Error en la importación'),
          description,
          variant: 'destructive',
        });

        throw error;
      } finally {
        setIsImporting(false);
      }
    },
    [t, toast],
  );

  return {
    importAccountingShipments,
    isImporting,
  };
};
